import { useContext, useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { CampaignContext, CampaignState } from '../../context/campaign';
import { deleteGuestBookEntry, fetchMyCampaign } from '../../services/campaign';
import { MyCampaignResponse } from '../../types/campaign';
import TableContainer from './components/TableContainer';
import { CampaignHeaderSection } from '../../shared/components/cards/CampaignItemsCard';
import { Loading } from '../../shared/components/Loading';
import moment from 'moment';
import { Button } from 'reactstrap';
import { renderIcon } from '~/shared/components/icons';
import { ConfirmModal } from '~/shared/components/ConfirmModal';
import toast from 'react-hot-toast';
import { NoDataCard } from './components/NoDataCard';

type DataType = {
    created_date: string;
    id: number;
    from: string;
    message: string;
};

const TITLE = `You don't have any guestbook messages yet`;
const BODY = `Here you can expect to find messages from guests to your campaign, whether they are supporters or well-wishers.`;

export const CampaignGuestbook = ({}: any) => {
    const [loading, setLoading] = useState(false);
    const [{ campaign }, campaignDispatch]: [CampaignState, any] =
        useContext(CampaignContext);
    const history = useHistory();
    const [deleteEntry, setDeleteEntry] = useState<DataType | null>(null);
    const [saving, setSaving] = useState<boolean>(false);

    function UpdatesText() {
        return (
            <div>
                <h1 className='h2'>Guestbook</h1>
            </div>
        );
    }

    const onDeleteUpdate = async () => {
        try {
            setSaving(true);
            if (deleteEntry) {
                const res = await deleteGuestBookEntry(deleteEntry.id);

                if (res.status === 204) {
                    const updatedCampaign = {
                        ...campaign,
                        guestbookEntries: campaign?.guestbookEntries.filter(
                            (entry) => entry.id !== deleteEntry.id
                        ),
                    };
                    campaignDispatch({
                        type: 'set_campaign',
                        payload: updatedCampaign,
                    });
                    setDeleteEntry(null);
                }
            }
        } catch (error) {
            toast.error('Problem occured while deleting guestbook!');
        } finally {
            setSaving(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                id: 'date',
                header: 'Date',
                accessorKey: 'created_date', // accessor is the "key" in the data
            },
            {
                id: 'from',
                header: 'From',
                accessorKey: 'from',
            },
            {
                id: 'message',
                header: 'Message',
                accessorKey: 'message',
            },
            {
                id: 'actions',
                header: '',
                accessorKey: '',
                accessorFn: (props: DataType) => {
                    return (
                        <span className='d-flex justify-content-end'>
                            <Button
                                onClick={() => {
                                    const foundEntry = data.find(
                                        (u) => u.id === props.id
                                    );
                                    if (foundEntry) setDeleteEntry(foundEntry);
                                }}
                                color='link'
                                className='p-1 text-danger'
                            >
                                {renderIcon('delete')}
                            </Button>
                        </span>
                    );
                },
            },
        ],
        []
    );

    const data: DataType[] = useMemo(() => {
        if (campaign?.guestbookEntries) {
            return campaign.guestbookEntries.map((entry) => {
                // original format is 'lll'
                return {
                    id: entry.id,
                    created_date: moment(entry.created_date).format(
                        'MM/D/Y hh:mm A'
                    ),
                    from: entry.signature,
                    message: entry.message,
                };
            });
        } else {
            return [];
        }
    }, [campaign]);

    useEffect(() => {
        if (!campaign) {
            getMyCampaign();
        }
    }, []);

    const getMyCampaign = async () => {
        try {
            const resp: MyCampaignResponse = await fetchMyCampaign();
            if (resp.detail && resp.detail === 'Invalid token.') {
                // Delete localStorage token
                localStorage.removeItem('token');
                history.push('/login');
            }
            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
        } catch (e) {
            toast.error('Error fetching campaign');
        } finally {
            setLoading(false);
        }
    };

    if (!campaign) {
        return (
            <div>
                <Loading />
            </div>
        );
    }

    const { campaignCoordinator } = campaign;

    return (
        <div className='container__inner'>
            <CampaignHeaderSection
                leftcard={<UpdatesText />}
                contact={campaignCoordinator}
            />
            <div className='pb-5'>
                <TableContainer
                    columns={columns}
                    data={data}
                    showPaginationComponents={true}
                    NoDataCard={<NoDataCard title={TITLE} body={BODY} />}
                    showHeaders
                />
            </div>

            <ConfirmModal
                isOpen={!!deleteEntry}
                title='Delete Message?'
                message='Are you sure you wish to delete this message?'
                toggle={() => setDeleteEntry(null)}
                handleConfirm={onDeleteUpdate}
                saving={saving}
                isDeleting={true}
            />
        </div>
    );
};
